/** Розширені фільтри (прототип v4, UIUX patterns/filtering): рядки «поле · умова · значення» ↔ рядок фільтр-DSL `поле:оператор:значення;…`. */
import { buildFilter } from '@platform/core/lib/filterDsl';
import type { IFieldDefinition, IRecordTypeDefinition } from '@platform/core/types';
import type { AdvancedOp } from './AdvancedOp';
import type { AdvancedValueKind } from './AdvancedValueKind';
import type { IAdvancedCondition } from './IAdvancedCondition';

const NO_VALUE: readonly AdvancedOp[] = ['empty', 'filled'];
const HIDDEN_TYPES: readonly string[] = ['textarea', 'json', 'file'];

/**
 * Поля, за якими можна фільтрувати: усі поля типу, крім довгого тексту, JSON, файлів (як у прототипі — без textarea) і прихованих; поля з
 * персональними даними — лише коли `pii` (Owner/Admin: useCanQueryPii; іншим сервер такий фільтр відхиляє). Чиста функція.
 */
export function filterableFields(rt: IRecordTypeDefinition, pii = true): IFieldDefinition[] {
  return rt.fields.filter((f) => !HIDDEN_TYPES.includes(f.type) && !f.hidden && (pii || !f.pii));
}

/** Умови, які API дозволяє для типу поля (05-API/filter-dsl.md, «Оператори за dataType»); посилання й шляхи — лише «порожнє / не порожнє». */
export function opsFor(field: IFieldDefinition | undefined): AdvancedOp[] {
  switch (field?.type) {
    case 'number': return ['eq', 'neq', 'lt', 'gt', 'empty', 'filled'];
    case 'date': return ['eq', 'lt', 'gt', 'empty', 'filled'];
    case 'select': return ['eq', 'neq', 'empty', 'filled'];
    case 'user': return ['eq', 'empty', 'filled'];
    case 'tags': return ['has', 'empty'];
    case 'boolean': return ['eq'];
    case 'ref': case 'lookup': case 'area': case 'iteration': return ['empty', 'filled'];
    default: return ['contains', 'eq', 'neq', 'empty', 'filled'];
  }
}

/** Поле значення для умови: без значення для «порожнє / не порожнє», інакше за типом поля. Чиста функція. */
export function valueKindOf(field: IFieldDefinition | undefined, op: AdvancedOp): AdvancedValueKind {
  if (NO_VALUE.includes(op)) return 'none';
  switch (field?.type) {
    case 'select': return 'option';
    case 'user': return 'user';
    case 'tags': return 'tag';
    case 'boolean': return 'boolean';
    case 'date': return 'date';
    case 'number': return 'number';
    default: return 'text';
  }
}

/** Чи рядок готовий до застосування: вибране поле і значення (якщо умова його потребує); неповні рядки не фільтрують. */
export function isComplete(c: IAdvancedCondition): boolean {
  return Boolean(c.field) && (NO_VALUE.includes(c.op) || c.value.trim() !== '');
}

/** Рядок фільтр-DSL із готових рядків (значення URL-кодуються, як у filterDsl.conditionToDsl). Чиста функція. */
export function conditionsToDsl(conditions: readonly IAdvancedCondition[]): string {
  return buildFilter(conditions.filter(isComplete).map((c) => ({ field: c.field, op: c.op, values: NO_VALUE.includes(c.op) ? [] : [c.value.trim()] })));
}

/** Рядки з рядка фільтр-DSL (зворотне до conditionsToDsl): `поле:оператор[:значення]` через `;`, значення декодуються. Чиста функція. */
export function dslToConditions(dsl: string): IAdvancedCondition[] {
  return dsl.split(';').map((s) => s.trim()).filter(Boolean).map((part) => {
    const [field = '', op = 'eq', ...rest] = part.split(':');
    return { field, op: op as AdvancedOp, value: decode(rest.join(':')) };
  });
}

/** Новий рядок: назва «містить» (як «+ Додати умову» у прототипі), інакше перше поле з його першою умовою. */
export function newCondition(rt: IRecordTypeDefinition): IAdvancedCondition {
  const fields = filterableFields(rt);
  const field = fields.find((f) => f.key === 'title') ?? fields[0];
  return { field: field?.key ?? '', op: opsFor(field)[0] ?? 'eq', value: '' };
}

/** Рядок після зміни поля: значення скидається, умова лишається, якщо нове поле її підтримує, інакше — перша умова поля. */
export function withField(c: IAdvancedCondition, field: IFieldDefinition | undefined): IAdvancedCondition {
  const ops = opsFor(field);
  return { field: field?.key ?? '', op: ops.includes(c.op) ? c.op : ops[0] ?? 'eq', value: '' };
}

function decode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
